import * as angular from "angular";
import {IController} from "angular";
import * as _ from "lodash";

export class BringozTimeDropdownComponent implements IController {
  form: any;
  timeStamp: any;
  referenceDate: any; // Reference date for correct date handling (uses initial timestamp)
  zoneId: string; // Entry's timezone (e.g., "America/New_York")
  min: any;
  max: any;
  isDisabled: boolean;
  name: string;
  ngPlaceholder?: string;
  onChange: Function;

  // Dropdown selections — selectedHour is always 0-23 internally
  public selectedHour: number = 0;
  public selectedMinute: number = 0;

  // Dropdown options
  public hourOptions: { value: number; label: string }[] = [];
  public minuteOptions: number[] = [];

  // Whether to use 12-hour format
  public is12Hour: boolean = false;

  constructor(private moment, private splitService) {
    // Detect 12h vs 24h from site time format config
    const timeFormat: string = this.splitService.getDateAndTimeConfig().time || 'HH:mm';
    this.is12Hour = timeFormat === 'hh:mm A';

    // Generate hour options
    this.hourOptions = [];
    _.times(24, (i) => {
      this.hourOptions.push({ value: i, label: this._formatHourLabel(i) });
    });

    // Generate minute options (0-59)
    this.minuteOptions = [];
    _.times(60, (i) => {
      this.minuteOptions.push(i);
    });
  }

  $onInit() {
    this._setTime(this.timeStamp);

    if (!this.name) {
      this.name = 'timeDropdown';
    }
  }

  $onChanges(changes) {
    if (changes.timeStamp && !changes.timeStamp.isFirstChange()) {
      this._setTime(this.timeStamp);
      this._checkValidity();
    }
    if ((changes.min && !changes.min.isFirstChange()) ||
      changes.max && !changes.max.isFirstChange()) {
      this._checkValidity();
    }
  }

  onTimeChange() {
    // Use referenceDate for the date part (has correct date in entry's timezone)
    // Fall back to timeStamp if referenceDate not provided
    const baseDateTimestamp = this.referenceDate || this.timeStamp;
    
    if (baseDateTimestamp && this.selectedHour !== undefined && this.selectedMinute !== undefined) {
      // Use entry's timezone (zoneId) first, fall back to global site timezone
      const timezone = this.zoneId || this.splitService.getTimeZone();
      
      // Parse timestamp in entry's timezone (critical for correct date handling)
      const baseMoment = this.moment.tz(baseDateTimestamp, timezone);
      const result = baseMoment
        .set({
          'hour': this.selectedHour,
          'minute': this.selectedMinute,
          'second': 0,
          'millisecond': 0
        });

      this.onChange({time: result.valueOf()});
    }
  }

  /**
   * Format hour label: "12 AM", "01 AM" ... "12 PM", "01 PM" for 12h; "00", "01" ... "23" for 24h
   */
  private _formatHourLabel(hour24: number): string {
    if (!this.is12Hour) {
      return hour24.toString().padStart(2, '0');
    }
    const period = hour24 >= 12 ? 'PM' : 'AM';
    let h = hour24 % 12;
    if (h === 0) h = 12;
    return h.toString().padStart(2, '0') + ' ' + period;
  }

  private _setTime(timeStamp) {
    if (timeStamp) {
      // Use entry's timezone (zoneId) first, fall back to global site timezone
      const timezone = this.zoneId || this.splitService.getTimeZone();
      const m = this.moment.tz(timeStamp, timezone);
      this.selectedHour = m.hour();
      this.selectedMinute = m.minute();
    } else {
      this.selectedHour = 0;
      this.selectedMinute = 0;
    }
  }

  private _checkValidity() {
    if (!this.form || !this.form[this.name]) {
      return;
    }

    const baseDateTimestamp = this.referenceDate || this.timeStamp;
    
    if (baseDateTimestamp && this.selectedHour !== undefined && this.selectedMinute !== undefined) {
      // Use entry's timezone (zoneId) first, fall back to global site timezone
      const timezone = this.zoneId || this.splitService.getTimeZone();
      const currentTime = this.moment.tz(baseDateTimestamp, timezone)
        .set({
          'hour': this.selectedHour,
          'minute': this.selectedMinute,
          'second': 0,
          'millisecond': 0
        }).valueOf();

      if (this.min) {
        this.form[this.name].$setValidity("min", currentTime >= this.min);
      }
      if (this.max) {
        this.form[this.name].$setValidity("max", currentTime <= this.max);
      }
    }
  }
}

angular
  .module('bringoz.admin.commons')
  .component('bringozTimeDropdown', {
    require: {
      form: '^form'
    },
    bindings: {
      timeStamp: '<?',
      referenceDate: '<?',
      zoneId: '<?',
      min: '<?',
      max: '<?',
      isDisabled: '<?',
      name: '<?',
      ngPlaceholder: '@?',
      onChange: '&?'
    },
    template: require('./bringoz-time-dropdown.html').default,
    controller: BringozTimeDropdownComponent
  });
